import { useNavigate } from "react-router-dom";
import { MoreHorizontal, Search } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import {
  boostTender,
  pinTender,
  unpinTender,
  tenderService,
  liveMarketService,
} from "../../services/api";
import ConfirmationModal from "./ConfirmationModal";
import PostedByAgentLine from "./PostedByAgentLine";
import RequestPreviewModal from "../common/RequestPreviewModal";
import AttachedDocLink from "../common/AttachedDocLink";
import CountryFlag from "../transfer-windows/CountryFlag";
import RequestDraftBadge from "./RequestDraftBadge";
import RequestDraftActions from "./RequestDraftActions";
import RequestArchivedBadge from "./RequestArchivedBadge";
import RequestArchiveActions from "./RequestArchiveActions";
import RequestConfirmDialog from "./RequestConfirmDialog";
import toast from "react-hot-toast";
import { usePermission } from "../../hooks/usePermission";
import { useClubTenderActions } from "../../hooks/clubtender/useClubTenderActions";
import {
  useTransferPointsCost,
  BOOST_COST,
  PIN_COST,
} from "../../hooks/clubtender/useTransferPointsCost";
import { useSelector } from "react-redux";
import { ROLES } from "../../config/roles";
import {
  formatSalary as formatSalaryShared,
  getSalaryKind,
  formatDivisionLine as formatDivisionLineShared,
  formatRange,
  formatLoanFee,
  isDraftRequest,
  isArchivedRequest,
} from "../../utils/tenderFormatters";
import { formatCurrencyWithSymbol } from "../../utils/currencyFormatter";
import { clubInitials } from "../../utils/clubInitials";

// The card's money renderer. It now delegates to the platform's shared
// formatter instead of building its own Intl.NumberFormat, so this card and
// the detail modal that opens from it (TenderDetailsModal → formatFeeBlock →
// formatCurrencyWithSymbol) print the identical string for the identical
// figure — apostrophe grouping, and CHF as a spaced text prefix (Marin,
// 2026-08-02: "CHF plattformweit rein danke" / "für jede Währung Apostrophen").
//
// The try/catch is gone with the Intl call that needed it: the shared
// formatter looks its symbol up in a table and falls back to "" for a code it
// does not know, so an unknown currency can no longer throw and crash the card.
//
// `!amount` still returns "-" — unchanged, and deliberately kept even though
// the shared formatter would render a real 0: on a request card a 0 fee has
// always read as "no figure given", and that is not this change's call to make.
const formatCurrency = (amount, currency) => {
  if (!amount) return "-";
  // Default-param won't kick in when caller passes null (only undefined).
  const code = typeof currency === "string" && currency.trim() ? currency.trim() : "USD";
  return formatCurrencyWithSymbol(amount, code);
};

const TenderCard = ({
  tender,
  type = "my-tenders",
  isStaffView = false,
  staffInClub = true,
  onSaveStatusChange,
  isNewest = false,
  // Request drafts: the club requests page hands its own transfer-window
  // payload down so the schedule picker's hint line reads the SAME window the
  // page header shows, and a refetch callback so a published draft actually
  // leaves the Drafts tab. Both are optional — every other mount point of this
  // card (agent/staff/coach surfaces) passes neither and is unaffected.
  transferWindow = null,
  onDraftChanged = null,
}) => {
  const navigate = useNavigate();
  const { currentRole } = useSelector((state) => state.auth);
  // A draft is the club's own unpublished row. Only the owner's "My Requests"
  // view can ever see one, so every draft branch below is gated on both.
  const isDraft = type === "my-tenders" && isDraftRequest(tender);
  // An archived row is the club's own ENDED row (Archive tab). Same gate:
  // only the owner's view carries the archive affordances.
  const isArchived = type === "my-tenders" && isArchivedRequest(tender);
  // The handoff dims an archived card's informational middle — the row has
  // ended, its facts are history, and the dim is what says so at a glance.
  const archivedDim = isArchived ? "opacity-60" : "";
  const {
    draftLoading,
    publishDraftNow,
    scheduleDraft,
    removeDraftSchedule,
    deleteDraft,
    closeRequest,
    reactivateRequest,
  } = useClubTenderActions(tender, { onChanged: onDraftChanged });
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);

  // Permission checks
  const canDeleteTenders = usePermission("delete_tenders");
  const canCreateTenders = usePermission("create_tenders");
  const canViewPitches = usePermission("view_pitches");
  const canContactClub = usePermission("contact_club");
  const [showMenu, setShowMenu] = useState(false);
  const [showBoostConfirmModal, setShowBoostConfirmModal] = useState(false);
  const [showPinConfirmModal, setShowPinConfirmModal] = useState(false);
  const { loadBalance, balanceLine, cannotAfford } = useTransferPointsCost();
  const [loading, setLoading] = useState(false);
  const menuRef = useRef(null);

  // Handle outside click to close the kebab menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  // Salary + kind come from the shared, null-safe formatter so the card and
  // the loupe preview never diverge (the old local logic dropped any bound
  // that was 0, e.g. net_salary_from=0 / net_salary_to=1000 rendered "-").
  const formatSalary = () => formatSalaryShared(tender) || "-";
  const getSalaryType = () => getSalaryKind(tender) || "Net";

  const formatMarketValue = () => {
    if (tender.market_value_type === "not_important") {
      return "Not important";
    }
    if (tender.market_value_type === "market_value_range") {
      return `${formatCurrency(
        tender.market_value_from,
        tender.currency
      )} - ${formatCurrency(tender.market_value_to, tender.currency)}`;
    } else if (tender.fix_market_value) {
      return formatCurrency(tender.fix_market_value, tender.currency);
    }
    return "-";
  };

  const formatPosition = (pos) => {
    return (
      pos
        ?.split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ") || "-"
    );
  };

  const handleViewPitches = () => {
    if (isStaffView) {
      // Check if current role is Coach, use coach routes
      if (currentRole === ROLES.Coach) {
        navigate(`/coach/tenders/${tender.id}/pitches`, { state: { tender } });
      } else {
        navigate(`/staff/tenders/${tender.id}/pitches`, { state: { tender } });
      }
    } else {
      navigate(`/club/tenders/${tender.id}/pitches`, { state: { tender } });
    }
  };

  // The "Apply Pitch" branch that used to live here (handleApplyPitch +
  // the isStaffView && !staffInClub arm of handlePrimaryAction) is GONE:
  // this card only ever renders PLAYER-category requests on the staff/coach
  // pages (staff rows go to StaffTenderCard), and the BE refuses a staff
  // self-pitch on a player request unconditionally ("Only agents can pitch
  // players", CreatePitchViewSet.perform_create). The button was dead and
  // misleading. Jobseekers no longer browse player requests at all (the
  // Players category is not offered to them); this is the second line of
  // defense for any path that still hands them a player card.

  // Archive instead of delete: ending a live request is the soft close the
  // server enforces (DELETE is refused for anything that was ever live). The
  // transition and its toast live in the shared hook; the page's refetch moves
  // the row into the Archive tab, with the old full reload only as the
  // fallback for a mount point that handed no refetch down.
  const handleCloseConfirm = async () => {
    const ok = await closeRequest();
    if (ok !== false) {
      setShowCloseModal(false);
      if (!onDraftChanged) window.location.reload();
    }
  };

  const handleReactivateConfirm = async () => {
    const ok = await reactivateRequest();
    if (ok !== false) {
      setShowReactivateModal(false);
      if (!onDraftChanged) window.location.reload();
    }
  };

  const handleMenuClick = () => {
    setShowMenu(!showMenu);
  };

  const handleBoostClick = () => {
    setShowMenu(false);
    setShowBoostConfirmModal(true);
    loadBalance();
  };

  const handlePinClick = () => {
    setShowMenu(false);
    setShowPinConfirmModal(true);
    // Unpinning is free, so it needs no balance and no gate.
    if (!tender.is_pinned) loadBalance();
  };

  const handleBoostConfirm = async () => {
    if (loading) return;
    setLoading(true);

    try {
      const response = await boostTender(tender.id);
      if (response.success) {
        toast.success("Request boosted successfully!");
        setShowBoostConfirmModal(false);
        // Update tender status locally
        tender.is_boosted = true;
      } else {
        toast.error(response.error || "Failed to boost request");
        // A rejection used to leave the dialog standing with the same text it
        // had before, so the club had no idea what to do next. Re-reading the
        // balance turns it into the answer: the line under the headline now
        // states the real figure and the confirm button goes dead if the
        // points are not there.
        loadBalance();
      }
    } catch (error) {
      console.error("Error boosting tender:", error);
      toast.error("Failed to boost request. Please try again.");
      loadBalance();
    } finally {
      setLoading(false);
    }
  };

  const handlePinConfirm = async () => {
    if (loading) return;
    setLoading(true);

    try {
      const response = tender.is_pinned
        ? await unpinTender(tender.id)
        : await pinTender(tender.id);

      if (response.success) {
        const action = tender.is_pinned ? "unpinned" : "pinned";
        toast.success(`Request ${action} successfully!`);
        setShowPinConfirmModal(false);
        // Update tender status locally
        tender.is_pinned = !tender.is_pinned;
        if (!tender.is_pinned && response.data?.pinned_tender_id) {
          tender.pinned_tender_id = response.data.pinned_tender_id;
        }
      } else {
        const action = tender.is_pinned ? "unpin" : "pin";
        toast.error(response.error || `Failed to ${action} request`);
        if (!tender.is_pinned) loadBalance();
      }
    } catch (error) {
      console.error("Error with pin/unpin tender:", error);
      const action = tender.is_pinned ? "unpin" : "pin";
      toast.error(`Failed to ${action} request. Please try again.`);
      if (!tender.is_pinned) loadBalance();
    } finally {
      setLoading(false);
    }
  };

  const handleCloseMenuClick = () => {
    setShowMenu(false);
    setShowCloseModal(true);
  };

  const handleReactivateMenuClick = () => {
    setShowMenu(false);
    setShowReactivateModal(true);
  };

  const handleMessageClub = async () => {
    if (loading) return;
    setLoading(true);
    try {
      console.log(tender);
      const clubId =
        tender?.club_id || tender?.club?.id || tender?.club?.club_id;
      if (!clubId) {
        toast.error("Club information not available for messaging");
        setLoading(false);
        return;
      }

      const response = await liveMarketService.createClubChat(clubId);
      if (response.success) {
        navigate("/inbox", {
          state: {
            channelId: response.channelId,
            channelType: response.channelType,
          },
        });
      } else {
        toast.error(response.error || "Failed to start chat with club");
      }
    } catch (error) {
      console.error("Error creating club chat:", error);
      toast.error("Failed to start chat with club");
    } finally {
      setLoading(false);
    }
  };

  const handlePrimaryAction = () => {
    handleMessageClub();
  };

  const handleClubClick = () => {
    const clubId = tender?.club?.wyscout_id || tender?.wyscout_id;
    if (clubId) {
      navigate(`/search/club/${clubId}`);
    }
  };

  // Save/bookmark a tender. Mirrors the agent + StaffTenderCard flow: confirm
  // modal, toggle via the same save_tender/unsave_tender endpoints, then let the
  // parent update is_saved (and drop it from the Saved list when unsaved).
  const handleSaveClick = () => {
    if (loading) return;
    setShowSaveConfirmModal(true);
  };

  const handleConfirmSave = async () => {
    setLoading(true);
    try {
      const response = tender.is_saved
        ? await tenderService.unsaveTender(tender.id)
        : await tenderService.saveTender(tender.id);

      if (response.success) {
        onSaveStatusChange?.(tender.id, !tender.is_saved);
      } else {
        toast.error(response.error || "Failed to update saved status");
      }
    } catch (error) {
      console.error("Error saving/unsaving tender:", error);
      toast.error("Failed to update saved status. Please try again.");
    } finally {
      setLoading(false);
      setShowSaveConfirmModal(false);
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg border p-6 flex flex-col min-h-[420px]">
        {/* Position and Boosted Badge */}
        <div className="flex justify-between items-start mb-4 gap-2">
          {/* flex-wrap + min-w-0: the badge row can now carry a
              "Scheduled · 15 Aug, 09:00" pill, which is wider than BOOSTED /
              LITE / NEWEST and would otherwise push the icon cluster off a
              320px screen. */}
          <div className="flex flex-wrap items-center gap-3 min-w-0">
            <h3 className="text-xl font-semibold capitalize text-[#0B0B0B] min-w-0 break-words">
              {formatPosition(tender.position)}
            </h3>

            {tender.is_boosted && (
              <span className="flex gap-2 font-medium items-center p-2 bg-gradient-to-r from-[#FF4D4F80] to-[#FAAA0080] text-[#000000] text-[10px] rounded-md capitalize">
                <svg
                  width="6"
                  height="10"
                  viewBox="0 0 6 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1.74942 6.25168H0.457751C0.291084 6.25168 0.16782 6.17703 0.0879592 6.02773C0.00809813 5.87842 0.0167787 5.73432 0.114001 5.59543L3.22858 1.11627C3.29803 1.01905 3.38831 0.951337 3.49942 0.913143C3.61053 0.874948 3.72511 0.876684 3.84317 0.918351C3.96122 0.960018 4.04803 1.03293 4.10358 1.1371C4.15914 1.24127 4.17997 1.35238 4.16608 1.47043L3.83275 4.16835H5.44733C5.62789 4.16835 5.75463 4.24821 5.82754 4.40793C5.90046 4.56766 5.87789 4.71696 5.75983 4.85585L2.33275 8.96002C2.25636 9.0503 2.16261 9.10932 2.0515 9.1371C1.94039 9.16488 1.83275 9.15446 1.72858 9.10585C1.62442 9.05724 1.54282 8.98259 1.48379 8.88189C1.42476 8.7812 1.4022 8.67182 1.41608 8.55377L1.74942 6.25168Z"
                    fill="#E11E4A"
                  />
                </svg>
                BOOSTED
              </span>
            )}

            {tender.is_lite && (
              <span className="px-2 py-1 bg-yellow-50 text-yellow-600 text-xs rounded-md capitalize">
                LITE
              </span>
            )}
            {isNewest && (
              <span className="px-2 py-0.5 bg-[#2283FF] text-white text-[10px] font-semibold rounded-full whitespace-nowrap">
                NEWEST
              </span>
            )}
            {/* Request drafts: the only marker that says this row is not live
                yet. Renders nothing for a published request. */}
            {isDraft && <RequestDraftBadge tender={tender} />}
            {/* Request archive: the marker that says this row has ended, with
                its archiving date. Renders nothing for a live row. */}
            {isArchived && <RequestArchivedBadge tender={tender} />}
          </div>

          <div className="flex items-center gap-4 mt-2">
            <AttachedDocLink tender={tender} size={4} />
            <button
              type="button"
              onClick={() => setShowPreview(true)}
              className="p-1.5 -m-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Preview request"
              title="Quick preview"
            >
              <Search className="w-4 h-4 text-[#535370]" />
            </button>
            {/* Save/bookmark: shown when browsing other clubs' requests
                (and in the Saved view), mirroring the agent experience. */}
            {type !== "my-tenders" && (
              <button
                onClick={handleSaveClick}
                disabled={loading}
                className={`p-1.5 -m-1.5 hover:bg-gray-100 rounded-lg transition-colors ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                aria-label={tender.is_saved ? "Unsave request" : "Save request"}
                title={tender.is_saved ? "Unsave request" : "Save request"}
              >
                <svg
                  className={`w-4 h-4 ${
                    tender.is_saved
                      ? "text-[#2283FF] fill-current"
                      : "text-[#1D1B20]"
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                  />
                </svg>
              </button>
            )}
            {type === "my-tenders" && (
              <div className="relative" ref={menuRef}>
                <MoreHorizontal
                  onClick={handleMenuClick}
                  className="w-4 h-4 cursor-pointer"
                />
                {showMenu && (
                  <div className="absolute right-0 top-6 bg-white py-4 px-2 shadow-lg border rounded-lg z-10 min-w-[160px]">
                    {/* Boost and Pin buy VISIBILITY in the agent feed. A draft
                        is in no feed at all, so neither can do anything for it
                        and neither is offered — and an archived row is in no
                        feed either, so its menu is Reactivate alone. Delete is
                        not offered for a draft here: it lives in the action
                        bar below, which deletes and refetches instead of
                        reloading the page. Edit Request stays exactly where it
                        was for live rows. */}
                    {/* Only show boost option if not already boosted */}
                    {!isDraft && !isArchived && !tender.is_boosted && (
                      <div
                        onClick={handleBoostClick}
                        className={`px-2 py-2 text-[#0A0F2D] text-sm cursor-pointer hover:bg-gray-50 ${
                          loading ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        {loading ? "Processing..." : "Boost this Request"}
                      </div>
                    )}
                    {/* Only show pin/unpin option if not already pinned, or show unpin if pinned */}
                    {isDraft || isArchived ? null : !tender.is_pinned ? (
                      <div
                        onClick={handlePinClick}
                        className={`px-2 py-2 text-[#0A0F2D] text-sm border-b border-[#D6DBEA] cursor-pointer hover:bg-gray-50 ${
                          loading ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        {loading ? "Processing..." : "Pin to top"}
                      </div>
                    ) : (
                      <div
                        onClick={handlePinClick}
                        className={`px-2 py-2 text-[#0A0F2D] text-sm border-b border-[#D6DBEA] cursor-pointer hover:bg-gray-50 ${
                          loading ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        {loading ? "Processing..." : "Unpin from top"}
                      </div>
                    )}
                    {!isArchived && canCreateTenders && (
                      <div
                        onClick={() =>
                          navigate(`/club/tenders/${tender.id}/edit`)
                        }
                        className="px-2 py-2 text-[#0A0F2D] text-sm cursor-pointer hover:bg-gray-50"
                      >
                        Edit Request
                      </div>
                    )}
                    {/* Ending a live request is Close, not Delete: it moves
                        the row to the archive, per the platform's archive-
                        instead-of-delete rule. Same permission gate the old
                        delete entry had. */}
                    {!isDraft && !isArchived && canDeleteTenders && (
                      <div
                        onClick={handleCloseMenuClick}
                        className="px-2 py-2 text-[#0A0F2D] text-sm cursor-pointer hover:bg-gray-50"
                      >
                        Close request
                      </div>
                    )}
                    {isArchived && (
                      <div
                        onClick={handleReactivateMenuClick}
                        className="px-2 py-2 text-[#0A0F2D] text-sm cursor-pointer hover:bg-gray-50"
                      >
                        Reactivate
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Club Info */}
        {/* min-w-0 so the name below can shrink instead of pushing the row out
            of the card — a flex child defaults to min-width:auto and refuses to
            go below its own content width, which is what let a long hyphenated
            club name overrun the card by 22px at 320px (measured). */}
        <div
          className="flex items-center gap-3 mb-4 min-w-0 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={handleClubClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClubClick();
            }
          }}
          tabIndex={0}
          role="button"
          aria-label={`View ${tender.club.name} profile`}
        >
          {/* No logo → initials badge, not a broken image. See
              TenderDetailsModal.js:144-154, which fixed the same `<img
              src={null}>` at its own call site; club.logo is null for every
              club without a linked Wyscout team. */}
          {tender.club.logo ? (
            <img
              src={tender.club.logo}
              alt={tender.club.name}
              className="w-10 h-10 rounded-full"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div
              className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-[#E9EEF6] text-[13px] font-bold text-[#6B7280]"
              aria-label={tender.club.name ? `${tender.club.name} crest` : "Club crest"}
            >
              {clubInitials(tender.club.name)}
            </div>
          )}
          <span className="min-w-0 break-words font-medium text-gray-900">
            {tender.club.name}
          </span>
        </div>

        {/* DECISION 009 — a request an AGENCY posted for this club must be
            distinguishable from one the club wrote itself. Sits OUTSIDE the
            club-info block above, which is a "view club profile" button — the
            attribution is text, not part of that control. Renders only when the
            backend sends agent_attribution, i.e. only on agency-posted rows. */}
        <PostedByAgentLine
          attribution={tender.agent_attribution}
          className="-mt-3 mb-4"
        />

        {/* Inactivity-warning banner: visible to the owner only ("my-tenders" view).
            Backend sets expiry_warning_sent_at; if the owner hasn't clicked
            "Keep active" since (kept_alive_at older or null), tender will be
            soft-expired ~48h after expiry_warning_sent_at. */}
        {type === "my-tenders" &&
          tender.status === "active" &&
          tender.expiry_warning_sent_at &&
          (!tender.kept_alive_at ||
            new Date(tender.kept_alive_at) <=
              new Date(tender.expiry_warning_sent_at)) && (
            <div className="mb-4 px-3 py-2 bg-[#FFF7ED] border border-[#FED7AA] rounded-lg flex items-center justify-between gap-3">
              <span className="text-[12px] text-[#9A3412] flex-1">
                ⚠ Inactive — will be removed in ~48 hours unless you confirm.
              </span>
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  if (loading) return;
                  setLoading(true);
                  try {
                    const res = await tenderService.keepTenderAlive(tender.id);
                    if (res.success) {
                      tender.kept_alive_at = new Date().toISOString();
                      toast.success("Request kept active.");
                    } else {
                      toast.error(res.error || "Failed to keep active");
                    }
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
                className="px-3 py-1 rounded-md bg-[#9A3412] text-white text-[12px] font-medium hover:bg-[#7C2D12] disabled:opacity-50"
              >
                Keep active
              </button>
            </div>
          )}

        {type !== "my-tenders" && formatDivisionLineShared(tender) && (
          <div className="flex items-center gap-2 mb-4">
            <p className="text-[#242426] text-sm font-medium">
              {formatDivisionLineShared(tender)}
            </p>
            <CountryFlag
              country={tender.division_country?.name}
              imageUrl={tender.division_country?.image_url}
              width={20}
              height={14}
            />
          </div>
        )}

        {/* Description (clamped on card; full text in preview modal + detail page) */}
        {/* The two-line clamp is the intended shortening on a card and stays.
            break-words is what makes the clamp honest: without it a URL or a
            long club name inside the description is one unbreakable token that
            sails past the clamp box and is cut mid-word instead of ellipsed. */}
        <p className={`text-gray-600 mb-4 overflow-hidden text-ellipsis line-clamp-2 break-words ${archivedDim}`}>
          {tender.description || "No description available"}
        </p>

        {/* Market Value & Type */}
        <div className={`flex justify-between items-center p-3 bg-[#F7F7F7] rounded-lg mb-4 ${archivedDim}`}>
          <div className="flex w-full justify-between items-center">
            <span className="text-[#1A212F] text-[14px] font-medium">
              {tender.type_of_tender === "buy"
                ? "Transfer Fee"
                : tender.type_of_tender === "loan"
                ? "Loan Fee"
                : "Market Value"}
            </span>
            <p className="text-[#000000] text-[14px] font-medium">
              {tender.type_of_tender === "buy"
                ? tender.maximum_transfer_fee
                  ? formatCurrency(tender.maximum_transfer_fee, tender.currency)
                  : "Free transfer"
                : tender.type_of_tender === "loan"
                ? formatLoanFee(tender, tender.currency)
                : formatMarketValue()}
            </p>
          </div>
        </div>

        {/* Essential Details */}
        <div className={`flex flex-col gap-3 mb-3 ${archivedDim}`}>
          <div className=" flex justify-between items-center ">
            <h1 className="text-[#242426B2] text-[12px] font-bold">Request Type</h1>
            <span
              className={`text-sm px-4 py-1 rounded-full capitalize ${
                tender.type_of_tender === "buy"
                  ? "bg-[#049669] text-white"
                  : tender.type_of_tender === "loan"
                  ? "bg-[#FF9500] text-white"
                  : "bg-blue-500 text-white"
              }`}
            >
              {tender.type_of_tender?.toUpperCase()}
            </span>
          </div>
          <div className="flex justify-between items-center ">
            <span className=" text-[#242426B2] text-[12px] font-bold">
              Games played last season
            </span>
            <p className="text-[12px] text-[#242426]">
              {tender.matches_played_last_season || "-"}
            </p>
          </div>
          <div className="flex justify-between items-center">
            <span className=" text-[#242426B2] text-[12px] font-bold">Age limit</span>
            <p className="text-[12px] text-[#242426] font-medium">
              {/* Use the shared null-safe formatter so a max-only limit
                  ("Up to 27 years") surfaces on the card, matching the
                  loupe/detail. The old `min_age ? ... : "-"` hid the age
                  whenever only max_age was set. */}
              {formatRange(tender.min_age, tender.max_age, "years") || "-"}
            </p>
          </div>
          <div className="flex justify-between items-center">
            <span className=" text-[#242426B2] text-[12px] font-bold">Foot</span>
            <p className="text-[12px] text-[#242426] font-medium">
              {tender.foot === "R"
                ? "Right"
                : tender.foot === "L"
                ? "Left"
                : tender.foot === "A"
                ? "Any"
                : "-"}
            </p>
          </div>
          <div className="flex justify-between items-center">
            <span className=" text-[#242426B2] text-[12px] font-bold">Min. Height</span>
            <p className="text-[12px] text-[#242426] font-medium">
              {tender.minimum_height ? `${tender.minimum_height} cm` : "-"}
            </p>
          </div>
          <div className="flex justify-between items-center">
            <span className=" text-[#242426B2] text-[12px] font-bold">
              {getSalaryType()} Monthly salary
            </span>
            <p className="text-[12px] text-[#242426] font-medium">{formatSalary()}</p>
          </div>
        </div>

        {/* Key Requirements */}
        <div className={`flex flex-col gap-3 mb-4 ${archivedDim}`}>
          <div className="flex justify-between items-start gap-2">
            <span className=" text-[#242426B2] text-[12px] font-bold whitespace-nowrap">
              Key Requirements
            </span>
            <p className="text-[12px] text-[#242426] font-medium min-w-0 text-right ml-2 overflow-hidden text-ellipsis line-clamp-2">
              {tender.key_requirements || "-"}
            </p>
          </div>
        </div>

        {/* Action Button */}
        <div className="mt-auto">
          {isDraft ? (
            /* A draft has no pitches to view. It gets the plan's own actions
               instead: preview what agents would see, publish it, move or
               remove its date, throw it away. */
            <RequestDraftActions
              tender={tender}
              transferWindow={transferWindow}
              isSaving={draftLoading}
              onPreview={() => setShowPreview(true)}
              onPostNow={publishDraftNow}
              onSchedule={scheduleDraft}
              onRemoveSchedule={removeDraftSchedule}
              onDelete={deleteDraft}
            />
          ) : isArchived ? (
            /* An archived request's history and its way back: the record
               (flips open below) and Reactivate live here instead of "View
               pitches". */
            <RequestArchiveActions
              tender={tender}
              isSaving={draftLoading}
              onReactivate={async () => {
                const ok = await reactivateRequest();
                if (ok !== false && !onDraftChanged) window.location.reload();
                return ok;
              }}
            />
          ) : type === "my-tenders" ? (
            <>
              {canViewPitches ? (
                <button
                  onClick={handleViewPitches}
                  className="w-full py-2 text-center border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  View pitches ({tender.total_pitches})
                </button>
              ) : (
                <div className="w-full py-2 text-center border border-gray-200 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed">
                  View pitches ({tender.total_pitches})
                </div>
              )}
            </>
          ) : isStaffView && !staffInClub ? null : (
            /* Clubless staff/coach viewers get NO action on a player card —
               the "Apply Pitch" that used to render here was refused by the
               BE every time (see the comment above handlePrimaryAction).
               The card stays fully viewable, only the dead button is gone. */
            <button
              onClick={handlePrimaryAction}
              disabled={loading || !canContactClub}
              className={`w-full py-2 text-center border bg-white border-[#E6E6E6] text-[#1E1E1E] rounded-lg ${
                loading || !canContactClub
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-gray-50"
              }`}
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full"></div>
                  <span>Connecting...</span>
                </div>
              ) : (
                <span>Message Club</span>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Close confirm — a plain confirm, deliberately without any "did it
          find its solution?" questions: the request wanders to the archive
          and stays reactivatable, that is the whole message. */}
      <RequestConfirmDialog
        isOpen={showCloseModal}
        title="Close request"
        body="The request moves to your archive, is kept for 12 months and can be reactivated at any time."
        confirmLabel="Close request"
        loading={draftLoading}
        onClose={() => setShowCloseModal(false)}
        onConfirm={handleCloseConfirm}
      />

      {/* Reactivate confirm for the kebab entry (the action-bar button brings
          its own inside RequestArchiveActions). */}
      <RequestConfirmDialog
        isOpen={showReactivateModal}
        title="Reactivate this request?"
        body="It goes live again, agents are notified, and fresh timers start."
        confirmLabel="Reactivate"
        loading={draftLoading}
        onClose={() => setShowReactivateModal(false)}
        onConfirm={handleReactivateConfirm}
      />

      {/* Save/Unsave Confirmation Modal (mirrors StaffTenderCard) */}
      {showSaveConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[400px]">
            <h3 className="text-lg font-semibold mb-2">
              {tender.is_saved ? "Unsave Request" : "Save Request"}
            </h3>
            <p className="text-gray-600 mb-6">
              {tender.is_saved
                ? "Are you sure you want to remove this request from your saved list?"
                : "Are you sure you want to save this request?"}
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowSaveConfirmModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSave}
                disabled={loading}
                className={`px-4 py-2 bg-[#2283FF] text-white rounded-lg hover:bg-blue-600 ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Boost Confirmation Modal */}
      <ConfirmationModal
        isOpen={showBoostConfirmModal}
        onClose={() => setShowBoostConfirmModal(false)}
        onConfirm={handleBoostConfirm}
        variant="v2"
        title="Boost Request"
        message={`Are you sure you want to boost this ${formatPosition(
          tender.position
        )} Request? This will cost ${BOOST_COST} Transfer Points.`}
        /* The dialog named the price but never the balance, so the club found
           out it could not pay only AFTER confirming. `lines` and
           `confirmDisabled` already exist on this dialog — nothing new had to
           be built, they were simply never used here. */
        lines={[balanceLine(BOOST_COST)]}
        confirmDisabled={cannotAfford(BOOST_COST)}
        confirmText="Boost Request"
        confirmButtonClass="bg-[#2283FF] hover:bg-blue-600"
        loading={loading}
      />

      {/* Pin Confirmation Modal */}
      <ConfirmationModal
        isOpen={showPinConfirmModal}
        onClose={() => setShowPinConfirmModal(false)}
        onConfirm={handlePinConfirm}
        variant="v2"
        title={tender.is_pinned ? "Unpin Request" : "Pin Request"}
        message={
          tender.is_pinned
            ? `Are you sure you want to unpin this ${formatPosition(
                tender.position
              )} Request?`
            : `Are you sure you want to pin this ${formatPosition(
                tender.position
              )} Request to the top? This will cost ${PIN_COST} Transfer Points.`
        }
        /* Unpinning costs nothing, so it gets neither the balance line nor the
           gate — only pinning does. */
        lines={tender.is_pinned ? [] : [balanceLine(PIN_COST)]}
        confirmDisabled={!tender.is_pinned && cannotAfford(PIN_COST)}
        confirmText={tender.is_pinned ? "Unpin Request" : "Pin Request"}
        confirmButtonClass="bg-[#2283FF] hover:bg-blue-600"
        loading={loading}
      />

      {/* Read-only quick-preview modal (loupe). Shows the full, untruncated
          tender + attached document, and mirrors the card's primary action. */}
      <RequestPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        tender={tender}
        primaryAction={
          isDraft ? (
            /* "This is how agents see your request" — the modal already IS the
               real preview component every surface uses, so the draft only
               swaps the CTA: publish it straight from here. */
            <button
              onClick={async () => {
                const ok = await publishDraftNow();
                if (ok !== false) setShowPreview(false);
              }}
              disabled={draftLoading}
              className={`w-full min-h-[44px] rounded-lg bg-[#2283FF] text-center font-semibold text-white hover:bg-blue-600 ${
                draftLoading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {draftLoading ? "Working..." : "Post now"}
            </button>
          ) : type === "my-tenders" ? (
            <button
              onClick={() => {
                if (!canViewPitches) return;
                setShowPreview(false);
                handleViewPitches();
              }}
              disabled={!canViewPitches}
              className={`w-full py-2 text-center border border-gray-200 rounded-lg ${
                canViewPitches
                  ? "hover:bg-gray-50"
                  : "bg-gray-100 text-gray-500 cursor-not-allowed"
              }`}
            >
              View pitches ({tender.total_pitches})
            </button>
          ) : isStaffView && !staffInClub ? null : (
            /* Same rule as the card's own action button above: no dead
               "Apply Pitch" for clubless staff/coach viewers. */
            <button
              onClick={() => {
                setShowPreview(false);
                handlePrimaryAction();
              }}
              disabled={loading || !canContactClub}
              className={`w-full py-2 text-center border bg-white border-[#E6E6E6] text-[#1E1E1E] rounded-lg ${
                loading || !canContactClub
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-gray-50"
              }`}
            >
              Message Club
            </button>
          )
        }
      />
    </>
  );
};

export default TenderCard;
